import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings } from '../services/api';

export default function ApiConfigForm() {
	const ALL_AGREEMENT_TYPES = [
		{ key: 'hirePurchase', label: 'Hire Purchase' },
		{ key: 'rentalAgreement', label: 'Rental Agreement' },
		{ key: 'mortgage', label: 'Mortgage' },
		{ key: 'creditCard', label: 'Credit Card' },
		{ key: 'fixedInstalment', label: 'Fixed Instalment' },
		{ key: 'loan', label: 'Loan' },
		{ key: 'utility', label: 'Utility' },
		{ key: 'budgetAccount', label: 'Budget Account' },
		{ key: 'currentAccount', label: 'Current Account' },
		{ key: 'secondMortgage', label: 'Second Mortgage' },
		{ key: 'unsecuredLoan', label: 'Unsecured Loan' },
		{ key: 'securedLoan', label: 'Secured Loan' },
		{ key: 'mail', label: 'Mail Order' },
		{ key: 'insurance', label: 'Insurance' },
		{ key: 'telecom', label: 'Telecom' },
	];

	const [form, setForm] = useState({
		production_api_key: '',
		staging_api_key: '',
		production_url: '',
		staging_url: '',
		active_environment: 'staging',
		show_public_claim_details: 'false',
		kount_staging_decision: 'allow',
		staging_enforce_reprocess_block: 'false',
		staging_show_debug: 'true',
		app_title: 'Valid8',
		enabled_agreement_types: ALL_AGREEMENT_TYPES.map(t => t.key).join(',')
	});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState(null);
	const [showStagingKey, setShowStagingKey] = useState(false);
	const [showProductionKey, setShowProductionKey] = useState(false);
	const [apiKeysOpen, setApiKeysOpen] = useState(true);

	useEffect(() => {
		loadSettings();
	}, []);

	async function loadSettings() {
		try {
			const data = await getSettings();
			setForm({
				production_api_key: data.production_api_key || '',
				staging_api_key: data.staging_api_key || '',
				production_url: data.production_url || '',
				staging_url: data.staging_url || '',
				active_environment: data.active_environment || 'staging',
				show_public_claim_details: data.show_public_claim_details || 'false',
				kount_staging_decision: data.kount_staging_decision || 'allow',
				staging_enforce_reprocess_block: data.staging_enforce_reprocess_block || 'false',
				staging_show_debug: data.staging_show_debug || 'true',
				app_title: data.app_title || 'Valid8',
				enabled_agreement_types: data.enabled_agreement_types || ALL_AGREEMENT_TYPES.map(t => t.key).join(',')
			});
		} catch (err) {
			setMessage({ type: 'error', text: 'Failed to load settings: ' + err.message });
		} finally {
			setLoading(false);
		}
	}

	async function handleSave(e) {
		e.preventDefault();
		const required = ['production_api_key', 'staging_api_key', 'production_url', 'staging_url'];
		const missing = required.filter(k => !form[k].trim());
		if (missing.length > 0) {
			setMessage({ type: 'error', text: `All fields are mandatory. Missing: ${missing.join(', ')}` });
			return;
		}
		setSaving(true);
		setMessage(null);
		try {
			await saveSettings(form);
			setMessage({ type: 'success', text: 'Settings saved successfully.' });
		} catch (err) {
			setMessage({ type: 'error', text: 'Failed to save settings: ' + err.message });
		} finally {
			setSaving(false);
		}
	}

	const handleChange = (field) => (e) => {
		setForm(prev => ({ ...prev, [field]: e.target.value }));
	};

	if (loading) return <p>Loading settings...</p>;

	const isStaging = form.active_environment === 'staging';

	return (
		<form onSubmit={handleSave} className="api-config-form">
			<h3>API Configuration</h3>

			{message && (
				<div className={`message ${message.type}`}>{message.text}</div>
			)}

			{/* App Title */}
			<div className="form-section">
				<h4>App Title</h4>
				<div className="form-field">
					<label>Application Title</label>
					<p className="form-hint">The title displayed in the navigation bar and browser tab.</p>
					<input
						type="text"
						value={form.app_title}
						onChange={handleChange('app_title')}
						placeholder="Valid8"
					/>
				</div>
			</div>

			{/* API Keys & URLs — Collapsible */}
			<div className="form-section">
				<div className="collapsible-header" onClick={() => setApiKeysOpen(!apiKeysOpen)}>
					<h4>
						<span className={`collapsible-arrow ${apiKeysOpen ? 'open' : ''}`}>&#9654;</span>
						API Keys &amp; URLs
					</h4>
					<span className="collapsible-hint">{apiKeysOpen ? 'Click to collapse' : 'Click to expand'}</span>
				</div>
				{apiKeysOpen && (
					<div className="collapsible-body">
						<div className="api-keys-grid">
							<div className="api-keys-column">
								<h5 className="api-keys-column-title">Staging</h5>
								<div className="form-field">
									<label>API Key *</label>
									<div className="input-with-toggle">
										<input
											type={showStagingKey ? 'text' : 'password'}
											value={form.staging_api_key}
											onChange={handleChange('staging_api_key')}
											placeholder="Enter staging API key"
											required
										/>
										<button type="button" onClick={() => setShowStagingKey(!showStagingKey)} className="btn-toggle-visibility">
											{showStagingKey ? 'Hide' : 'Show'}
										</button>
									</div>
								</div>
								<div className="form-field">
									<label>URL *</label>
									<input
										type="url"
										value={form.staging_url}
										onChange={handleChange('staging_url')}
										placeholder="https://staging-api.example.com"
										required
									/>
								</div>
							</div>
							<div className="api-keys-column">
								<h5 className="api-keys-column-title">Production</h5>
								<div className="form-field">
									<label>API Key *</label>
									<div className="input-with-toggle">
										<input
											type={showProductionKey ? 'text' : 'password'}
											value={form.production_api_key}
											onChange={handleChange('production_api_key')}
											placeholder="Enter production API key"
											required
										/>
										<button type="button" onClick={() => setShowProductionKey(!showProductionKey)} className="btn-toggle-visibility">
											{showProductionKey ? 'Hide' : 'Show'}
										</button>
									</div>
								</div>
								<div className="form-field">
									<label>URL *</label>
									<input
										type="url"
										value={form.production_url}
										onChange={handleChange('production_url')}
										placeholder="https://api.example.com"
										required
									/>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Active Environment */}
			<div className="form-section">
				<h4>Active Environment</h4>
				<div className="toggle-group">
					<label className={form.active_environment === 'staging' ? 'active' : ''}>
						<input
							type="radio"
							name="active_environment"
							value="staging"
							checked={isStaging}
							onChange={handleChange('active_environment')}
						/>
						Staging
					</label>
					<label className={form.active_environment === 'production' ? 'active' : ''}>
						<input
							type="radio"
							name="active_environment"
							value="production"
							checked={!isStaging}
							onChange={handleChange('active_environment')}
						/>
						Production
					</label>
				</div>

				{/* Staging-only settings nested under Active Environment */}
				{isStaging && (
					<div className="staging-only-section">
						<div className="staging-only-banner">
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v2m0 4h.01M5.07 19h13.86c1.15 0 1.86-1.24 1.28-2.24L13.28 3.52a1.44 1.44 0 0 0-2.56 0L3.79 16.76c-.58 1-.13 2.24 1.28 2.24z"/></svg>
							Staging Use Only
						</div>

						<div className="form-field">
							<label>Enforce Reprocessing Block</label>
							<p className="form-hint">
								When enabled, clients who have already been successfully processed (ALLOW) will be blocked from running again, just like in production. When disabled, clients can be reprocessed freely for testing.
							</p>
							<div className="toggle-group">
								<label className={form.staging_enforce_reprocess_block === 'false' ? 'active' : ''}>
									<input type="radio" name="staging_enforce_reprocess_block" value="false"
										checked={form.staging_enforce_reprocess_block === 'false'}
										onChange={handleChange('staging_enforce_reprocess_block')} />
									Off (allow reprocessing)
								</label>
								<label className={form.staging_enforce_reprocess_block === 'true' ? 'active' : ''}>
									<input type="radio" name="staging_enforce_reprocess_block" value="true"
										checked={form.staging_enforce_reprocess_block === 'true'}
										onChange={handleChange('staging_enforce_reprocess_block')} />
									On (block reprocessing)
								</label>
							</div>
						</div>

						<div className="form-field">
							<label>Kount Decision Override</label>
							<p className="form-hint">
								Controls the Kount NAO outcome in staging. The decision is passed via the <code>accountCreationUrl</code> field. Ignored in production.
							</p>
							<div className="toggle-group">
								<label className={form.kount_staging_decision === 'allow' ? 'active' : ''}>
									<input type="radio" name="kount_staging_decision" value="allow"
										checked={form.kount_staging_decision === 'allow'}
										onChange={handleChange('kount_staging_decision')} />
									Allow
								</label>
								<label className={form.kount_staging_decision === 'challenge' ? 'active' : ''}>
									<input type="radio" name="kount_staging_decision" value="challenge"
										checked={form.kount_staging_decision === 'challenge'}
										onChange={handleChange('kount_staging_decision')} />
									Challenge
								</label>
								<label className={form.kount_staging_decision === 'block' ? 'active' : ''}>
									<input type="radio" name="kount_staging_decision" value="block"
										checked={form.kount_staging_decision === 'block'}
										onChange={handleChange('kount_staging_decision')} />
									Block
								</label>
							</div>
						</div>
						<div className="form-field">
							<label>Show Debug Panel on Public Credit Check</label>
							<p className="form-hint">
								When enabled, the public credit check page will display a step-by-step debug panel showing payloads and responses. Always hidden in production.
							</p>
							<div className="toggle-group">
								<label className={form.staging_show_debug === 'false' ? 'active' : ''}>
									<input type="radio" name="staging_show_debug" value="false"
										checked={form.staging_show_debug === 'false'}
										onChange={handleChange('staging_show_debug')} />
									Off
								</label>
								<label className={form.staging_show_debug === 'true' ? 'active' : ''}>
									<input type="radio" name="staging_show_debug" value="true"
										checked={form.staging_show_debug === 'true'}
										onChange={handleChange('staging_show_debug')} />
									On
								</label>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Public Credit Check Page */}
			<div className="form-section">
				<h4>Public Credit Check Page</h4>
				<div className="form-field">
					<label>Show Claim Details to Client</label>
					<p className="form-hint">
						When enabled, clients will see the number of claims found after a successful credit check. When disabled, they will only see a success message.
					</p>
					<div className="toggle-group">
						<label className={form.show_public_claim_details === 'false' ? 'active' : ''}>
							<input type="radio" name="show_public_claim_details" value="false"
								checked={form.show_public_claim_details === 'false'}
								onChange={handleChange('show_public_claim_details')} />
							Off
						</label>
						<label className={form.show_public_claim_details === 'true' ? 'active' : ''}>
							<input type="radio" name="show_public_claim_details" value="true"
								checked={form.show_public_claim_details === 'true'}
								onChange={handleChange('show_public_claim_details')} />
							On
						</label>
					</div>
				</div>
			</div>

			{/* Claim Agreement Types */}
			<div className="form-section">
				<h4>Claim Agreement Types</h4>
				<div className="form-field">
					<label>Enabled Agreement Types</label>
					<p className="form-hint">
						Select which Equifax insightData agreement types should be extracted as claims. Only checked types will be processed. Uncheck types you want to exclude.
					</p>
					<div className="agreement-types-grid">
						{ALL_AGREEMENT_TYPES.map(type => {
							const enabledList = (form.enabled_agreement_types || '').split(',').filter(Boolean);
							const isChecked = enabledList.includes(type.key);
							return (
								<label key={type.key} className={`agreement-type-checkbox ${isChecked ? 'checked' : ''}`}>
									<input
										type="checkbox"
										checked={isChecked}
										onChange={(e) => {
											const current = (form.enabled_agreement_types || '').split(',').filter(Boolean);
											let updated;
											if (e.target.checked) {
												updated = [...current, type.key];
											} else {
												updated = current.filter(k => k !== type.key);
											}
											setForm(prev => ({ ...prev, enabled_agreement_types: updated.join(',') }));
										}}
									/>
									<span className="agreement-type-text">
										<span className="agreement-type-label">{type.label}</span>
										<span className="agreement-type-key">{type.key}</span>
									</span>
								</label>
							);
						})}
					</div>
					<div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
						<button type="button" className="btn-secondary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}
							onClick={() => setForm(prev => ({ ...prev, enabled_agreement_types: ALL_AGREEMENT_TYPES.map(t => t.key).join(',') }))}>
							Select All
						</button>
						<button type="button" className="btn-secondary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}
							onClick={() => setForm(prev => ({ ...prev, enabled_agreement_types: '' }))}>
							Deselect All
						</button>
					</div>
				</div>
			</div>

			<button type="submit" disabled={saving} className="btn-primary">
				{saving ? 'Saving...' : 'Save Settings'}
			</button>
		</form>
	);
}
